import React, { useState } from 'react';
import { MessageSquareIcon, EyeIcon, UserIcon, UsersIcon, GlobeIcon, LockIcon } from 'lucide-react';
export type NoteVisibility = 'Public' | 'Internal' | 'Role-based' | 'User-specific' | 'Creator-only';
interface StandardizedNotesProps {
  value: string;
  onChange: (value: string) => void;
  visibility?: NoteVisibility;
  onVisibilityChange?: (visibility: NoteVisibility) => void;
  visibilityDetails?: string;
  onVisibilityDetailsChange?: (details: string) => void;
  placeholder?: string;
  label?: string;
  roles?: {
    id: string;
    name: string;
  }[];
  users?: {
    id: string;
    name: string;
  }[];
  className?: string;
  rows?: number;
  disabled?: boolean;
  required?: boolean;
}
export function StandardizedNotes({
  value,
  onChange,
  visibility = 'Internal',
  onVisibilityChange,
  visibilityDetails = '',
  onVisibilityDetailsChange,
  placeholder = 'Add notes here...',
  label = 'Notes',
  roles = [],
  users = [],
  className = '',
  rows = 3,
  disabled = false,
  required = false
}: StandardizedNotesProps) {
  const [showVisibilityOptions, setShowVisibilityOptions] = useState(false);
  // Default roles if none provided
  const defaultRoles = [{
    id: 'finance',
    name: 'Finance Team'
  }, {
    id: 'procurement',
    name: 'Procurement Team'
  }, {
    id: 'management',
    name: 'Management'
  }, {
    id: 'auditor',
    name: 'Auditors'
  }];
  // Default users if none provided
  const defaultUsers = [{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Robert Brown'
  }];
  const actualRoles = roles.length > 0 ? roles : defaultRoles;
  const actualUsers = users.length > 0 ? users : defaultUsers;
  const getVisibilityIcon = () => {
    switch (visibility) {
      case 'Public':
        return <GlobeIcon className="h-4 w-4 text-green-500" data-id="element-1980" />;
      case 'Internal':
        return <UsersIcon className="h-4 w-4 text-blue-500" data-id="element-1981" />;
      case 'Role-based':
        return <UsersIcon className="h-4 w-4 text-purple-500" data-id="element-1982" />;
      case 'User-specific':
        return <UserIcon className="h-4 w-4 text-orange-500" data-id="element-1983" />;
      case 'Creator-only':
        return <LockIcon className="h-4 w-4 text-red-500" data-id="element-1984" />;
      default:
        return <EyeIcon className="h-4 w-4 text-gray-500" data-id="element-1985" />;
    }
  };
  const getVisibilityLabel = () => {
    switch (visibility) {
      case 'Public':
        return 'Public - Visible to everyone including external users';
      case 'Internal':
        return 'Internal - Visible to all internal users';
      case 'Role-based':
        return 'Role-based - Visible only to specific roles';
      case 'User-specific':
        return 'User-specific - Visible only to specific users';
      case 'Creator-only':
        return 'Private - Visible only to you';
      default:
        return 'Select visibility';
    }
  };
  return <div className={`${className}`} data-id="element-1986">
      <div className="flex justify-between items-center mb-1" data-id="element-1987">
        <label className="block text-sm font-medium text-gray-700" data-id="element-1988">
          {label} {required && <span className="text-red-500" data-id="element-1989">*</span>}
        </label>
        {onVisibilityChange && <div className="relative" data-id="element-1990">
            <button type="button" onClick={() => setShowVisibilityOptions(!showVisibilityOptions)} className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50" data-id="element-1991">
              {getVisibilityIcon()}
              <span className="ml-1" data-id="element-1992">Visibility</span>
            </button>
            {showVisibilityOptions && <div className="absolute right-0 mt-1 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-1993">
                <div className="py-1" data-id="element-1994">
                  <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-1995">
                    Select who can see this note
                  </div>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Public' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Public');
              setShowVisibilityOptions(false);
            }} data-id="element-1996">
                    <GlobeIcon className="h-4 w-4 mr-2 text-green-500" data-id="element-1997" />
                    Public
                    <span className="ml-1 text-xs text-gray-500" data-id="element-1998">
                      (Everyone)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Internal' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Internal');
              setShowVisibilityOptions(false);
            }} data-id="element-1999">
                    <UsersIcon className="h-4 w-4 mr-2 text-blue-500" data-id="element-2000" />
                    Internal
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2001">
                      (All staff)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Role-based' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Role-based');
              setShowVisibilityOptions(false);
            }} data-id="element-2002">
                    <UsersIcon className="h-4 w-4 mr-2 text-purple-500" data-id="element-2003" />
                    Role-based
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2004">
                      (Specific roles)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'User-specific' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('User-specific');
              setShowVisibilityOptions(false);
            }} data-id="element-2005">
                    <UserIcon className="h-4 w-4 mr-2 text-orange-500" data-id="element-2006" />
                    User-specific
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2007">
                      (Select users)
                    </span>
                  </button>
                  <button type="button" className={`flex items-center w-full text-left px-4 py-2 text-sm ${visibility === 'Creator-only' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} onClick={() => {
              onVisibilityChange('Creator-only');
              setShowVisibilityOptions(false);
            }} data-id="element-2008">
                    <LockIcon className="h-4 w-4 mr-2 text-red-500" data-id="element-2009" />
                    Private
                    <span className="ml-1 text-xs text-gray-500" data-id="element-2010">
                      (Only you)
                    </span>
                  </button>
                </div>
              </div>}
          </div>}
      </div>
      {/* Visibility indicator */}
      {onVisibilityChange && <div className="flex items-center mb-2 text-xs text-gray-500" data-id="element-2011">
          {getVisibilityIcon()}
          <span className="ml-1" data-id="element-2012">{getVisibilityLabel()}</span>
        </div>}
      {/* Role or User selection when applicable */}
      {onVisibilityDetailsChange && visibility === 'Role-based' && <div className="mb-2" data-id="element-2013">
          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2014">
            Select roles who can view this note:
          </label>
          <select className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" value={visibilityDetails} onChange={e => onVisibilityDetailsChange(e.target.value)} multiple size={3} data-id="element-2015">
            {actualRoles.map(role => <option key={role.id} value={role.id} data-id="element-2016">
                {role.name}
              </option>)}
          </select>
        </div>}
      {onVisibilityDetailsChange && visibility === 'User-specific' && <div className="mb-2" data-id="element-2017">
          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-2018">
            Select users who can view this note:
          </label>
          <select className="block w-full py-1 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" value={visibilityDetails} onChange={e => onVisibilityDetailsChange(e.target.value)} multiple size={3} data-id="element-2019">
            {actualUsers.map(user => <option key={user.id} value={user.id} data-id="element-2020">
                {user.name}
              </option>)}
          </select>
        </div>}
      {/* Notes textarea */}
      <div className="relative" data-id="element-2021">
        <div className="absolute top-2 left-2 text-gray-400" data-id="element-2022">
          <MessageSquareIcon className="h-5 w-5" data-id="element-2023" />
        </div>
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} className="block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder={placeholder} disabled={disabled} data-id="element-2024" />
      </div>
    </div>;
}